import {
  Box,
  Button,
  ColumnLayout,
  Container,
  DatePicker,
  Form,
  FormField,
  Select,
  SelectProps,
  SpaceBetween,
  Spinner,
  TextContent,
} from "@cloudscape-design/components-themed";
import { ClickDetail } from "@cloudscape-design/components-themed/internal/events";
import { useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hook";
import { RootState } from "../../app/store";
import {
  useGetCreditCardExaminationFileQuery,
  usePostCreditCardExaminationFileMutation,
} from "../../features/api";
import { Messages, getMessage } from "../../features/error/messages";
import { addNotification } from "../../features/error/notifications-slice";
import {
  InputNames,
  clearFormFieldErrTxt,
} from "../../features/error/validation-slice";
import { getExt } from "../../utils/common";
import { formatDate, formatLabel } from "../../utils/format";
import { isLengthFixedValid, isNull } from "../../utils/inputValidation";
import { FileUpload } from "../UI/file-upload/FileUpload";
import { showModal } from "../UI/modal/modal-slice";
import { COMMON_LABELS, COMMON_CONFIGS } from "../default-constants";
import SearchBar from "./SearchBar";
import Table from "./Table";
import { CONFIGS, CONTENTS, LABELS, ORDER_TARGET_LIST } from "./constants";

export interface FileItem {
  label: string;
  dismissLabel: string;
}

/**
 * クレカ申請審査申請回答登録画面(主に登録部分)
 */
const Main = () => {
  const dispatch = useAppDispatch();
  const [postCreditCardExaminationFileRegister] =
    usePostCreditCardExaminationFileMutation();

  // 回答日
  const [responseDate, setResponseDate] = useState("");

  //提出用ファイル
  const [uploadFile, setUploadFile] = useState<File>();

  //仕向け先エラーメッセージ
  const [orderTargetErrorMsg, setOrderTargetErrorMsg] = useState<string>("");

  //回答日エラーメッセージ
  const [responseDateErrorMsg, setResponseDateErrorMsg] = useState<string>("");

  // ファイル参照部分エラーテキスト
  const [fileErrorText, setFileErrorText] = useState("");

  // inputRef
  const inputRef = useRef<HTMLInputElement>(null);

  // スピナー表示処理用
  const [isSpinnerDisplay, setIsSpinnerDisplay] = useState(false);
  // ボタン非活性用
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  // アップロード対象ブランド
  const [uploadTargetBrand, setUploadTargetBrand] =
    useState<SelectProps.Option>({
      label: "",
      value: "",
    });

  // 入力エラー表示用変数
  const selectItem = CONFIGS.SELECT_ITEM as keyof InputNames;

  //一覧データの処理
  const searchCondition = useAppSelector(
    (state: RootState) => state.creditCardExaminationSearchCondition
  );

  //一覧データの取得
  const { data, isFetching, refetch } =
    useGetCreditCardExaminationFileQuery(searchCondition);

  // 提出用ファイル設定
  const onChangeStoreFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;

    if (!files?.[0]) {
      return;
    }

    // アップロードされたファイルの拡張子が「.xlsx」「.csv」以外の場合はアラートを出して終了する。
    if (!fileExtentCheck(files, uploadTargetBrand.label)) {
      return;
    }

    // 送信するファイル情報を設定
    setUploadFile(files[0]);

    // エラーテキストを設定
    setFileErrorText(LABELS.ERROR_TEXT_LABEL);
  };

  // ファイルの拡張子をチェック
  const fileExtentCheck = (
    files: FileList,
    uploadTargetBrand: string | undefined
  ) => {
    let checkResultFlg = true;
    let messageId = "" as keyof Messages;
    let targetBrandSmccFlg = false;

    // 仕向け先ごとに許容するファイル拡張子を確認
    if (uploadTargetBrand === "クレカ依頼（SMCC）") {
      targetBrandSmccFlg = true;
    }

    if (targetBrandSmccFlg) {
      // 「.xlsx」「.csv」を許容
      if (
        CONFIGS.FILE_EXTENSION_EXCEL !== getExt(files[0].name.toLowerCase()) &&
        CONFIGS.FILE_EXTENSION_CSV !== getExt(files[0].name.toLowerCase())
      ) {
        messageId = "KGRERR097" as keyof Messages;
        checkResultFlg = false;
      }
    } else {
      // 「.xlsx」のみ許容
      if (
        CONFIGS.FILE_EXTENSION_EXCEL !== getExt(files[0].name.toLowerCase())
      ) {
        messageId = "KGRERR057" as keyof Messages;
        checkResultFlg = false;
      }
    }

    if (!checkResultFlg) {
      // アラート処理
      dispatch(
        addNotification({
          msgId: messageId,
          msgArgs: [],
        })
      );
    }

    return checkResultFlg;
  };

  // 参照ボタンイベント
  const uploadOnClick = (e: CustomEvent<ClickDetail>) => {
    e.preventDefault();
    // ファイルアップロード処理
    inputRef.current?.click();
  };

  // 登録ボタン押下処理
  const onClickButton = async () => {
    const fileData = new FormData();
    if (uploadFile === undefined) {
      // ファイル未選択時のエラー表示
      const messageId = "KGRERR054" as keyof Messages;
      // アラート処理
      dispatch(
        addNotification({
          msgId: messageId,
          msgArgs: [],
        })
      );
      return;
    }

    // 確認ダイアログ表示メッセージ作成
    let contentMessage = COMMON_LABELS.CONTENTS.APPLICATION_CONFIRM;
    let dateLabel = formatDate(responseDate);

    if (
      uploadFile !== undefined ||
      uploadTargetBrand.value !== undefined ||
      uploadTargetBrand.label !== undefined ||
      uploadTargetBrand.value !== "" ||
      uploadTargetBrand.label !== ""
    ) {
      // アップロードファイル設定処理
      fileData.append(CONFIGS.FILE, uploadFile);
      fileData.append(
        CONFIGS.ORDER_TARGET_CODE,
        uploadTargetBrand.value as string
      );
      fileData.append(CONFIGS.APPLICATION_RESPONSE_DATE, dateLabel);
      //　確認ダイアログ表示メッセージ追記
      contentMessage += formatLabel(CONTENTS.APPLICATION_CONFIRM, [
        uploadTargetBrand.label,
        uploadFile ? uploadFile.name : "",
      ]);
    }

    //バリデーションチェック
    if (!checkItems()) return;

    //　確認ダイアログ表示
    dispatch(
      showModal({
        title: COMMON_LABELS.TITLES.DL,
        content: contentMessage,
        negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
        positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
        positiveFn: () => {
          creditCardExaminationFileRegister(fileData);
        },
      })
    );
    /** 同じファイルを連続アップロードできるように処理 */
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  // 確認ボタンOK押下時の処理
  const creditCardExaminationFileRegister = async (fileData: FormData) => {
    //スピナー
    setIsSpinnerDisplay(true);
    // ボタン非活性
    setIsButtonDisabled(true);

    //エラーテキスト初期化
    setFileErrorText("");

    //登録処理呼び出し
    postCreditCardExaminationFileRegister(fileData)
      .unwrap()
      .then((res) => {
        // アップロード後初期化
        setUploadFile(undefined);

        if (COMMON_CONFIGS.API.STATUS_SUCCESS === res.status) {
          //一覧データを更新
          refetch();
        }
      })
      .finally(() => {
        // スピナー停止
        setIsSpinnerDisplay(false);
        // ボタン活性
        setIsButtonDisabled(false);
      });
  };

  //バリエーションチェック
  const checkItems = () => {
    let checkResult = true;
    if (!checkResponseDate(responseDate, setResponseDateErrorMsg, true)) {
      checkResult = false;
    }
    if (
      undefined === uploadTargetBrand.value ||
      "" === uploadTargetBrand.value
    ) {
      setOrderTargetErrorMsg(getMessage("KGRERR055").label);
      checkResult = false;
    } else {
      setOrderTargetErrorMsg("");
    }
    return checkResult;
  };

  // 回答日の入力チェック
  const checkResponseDate = (
    target: string,
    paramSetErrorMsg: any,
    checkLengthFlag: boolean
  ) => {
    const setErrorMsg = paramSetErrorMsg;
    // 必須
    if (isNull(target)) {
      setErrorMsg(getMessage("KGRERR055").label);
      return false;
    }
    // 文字長(入力時の文字数チェックが不要)
    if (checkLengthFlag) {
      if (!isLengthFixedValid(target, 10)) {
        setErrorMsg(getMessage("KGRERR073", 10).label);
        return false;
      }
    }
    setErrorMsg("");
    return true;
  };

  return (
    <SpaceBetween direction="vertical" size="l">
      <Container>
        <SpaceBetween direction="vertical" size="xxl">
          <SpaceBetween direction="vertical" size="xxl">
            <Form
              actions={
                <SpaceBetween direction="horizontal" size="l">
                  {isSpinnerDisplay ? <Spinner size="big" /> : <></>}
                  <Button
                    iconAlign="right"
                    onClick={onClickButton}
                    variant="primary"
                    disabled={isButtonDisabled}
                  >
                    {COMMON_LABELS.BTNS.REGISTER}
                  </Button>
                </SpaceBetween>
              }
            >
              <SpaceBetween direction="vertical" size="xxl">
                <ColumnLayout columns={4}>
                  <FormField
                    label={
                      <SpaceBetween direction="horizontal" size="xs">
                        <Box variant="strong">{LABELS.ORDER_TARGET_NAME}</Box>
                        <Box variant="small" color="text-status-error">
                          {COMMON_LABELS.INPUT_ITEM_MESSAGE.REQUIRED}
                        </Box>
                      </SpaceBetween>
                    }
                    errorText={orderTargetErrorMsg}
                  >
                    <Select
                      selectedOption={uploadTargetBrand}
                      options={ORDER_TARGET_LIST}
                      filteringType="none"
                      onChange={(event: any) => {
                        dispatch(clearFormFieldErrTxt(selectItem));
                        setUploadTargetBrand(event.detail.selectedOption);
                      }}
                    />
                  </FormField>
                </ColumnLayout>
                <SpaceBetween direction="vertical" size="xxxs">
                  <TextContent>
                    <h4>{LABELS.APPLICATION_FILE}</h4>
                  </TextContent>
                  <FileUpload
                    inputRef={inputRef}
                    label={""}
                    name={"corporateFile"}
                    placeholder={LABELS.FILE_PLACEHOLDER}
                    accept={CONFIGS.FILE_EXTENSION_EXCEL_CSV}
                    fileName={uploadFile ? uploadFile.name : ""}
                    setUploadFile={setUploadFile}
                    errorText={fileErrorText}
                    setErrorText={setFileErrorText}
                    onChange={onChangeStoreFile}
                    onUpload={uploadOnClick}
                  />
                  <SpaceBetween direction="horizontal" size="l">
                    <FormField
                      label={
                        <SpaceBetween direction="horizontal" size="xs">
                          <Box variant="strong">{LABELS.RESPONSE_DATE}</Box>
                          <Box variant="small" color="text-status-error">
                            {COMMON_LABELS.INPUT_ITEM_MESSAGE.REQUIRED}
                          </Box>
                        </SpaceBetween>
                      }
                      errorText={responseDateErrorMsg}
                    >
                      <DatePicker
                        onChange={({ detail }) => setResponseDate(detail.value)}
                        value={responseDate}
                        openCalendarAriaLabel={(selectedDate) =>
                          "Choose certificate expiry date" +
                          (selectedDate
                            ? `, selected date is ${selectedDate}`
                            : "")
                        }
                        nextMonthAriaLabel="Next month"
                        placeholder="YYYY/MM/DD"
                        previousMonthAriaLabel="Previous month"
                        todayAriaLabel="Today"
                      />
                    </FormField>
                  </SpaceBetween>
                </SpaceBetween>
              </SpaceBetween>
            </Form>
          </SpaceBetween>
        </SpaceBetween>
      </Container>
      <SearchBar />
      <Table data={data} isFetching={isFetching} refetch={refetch}></Table>
    </SpaceBetween>
  );
};

export default Main;
